import React, { useEffect, useRef, useState } from 'react';
import ModalErrorV2 from '../../Modals/ModalErrorV2';
import { readableFColor } from '../../../../util/readableBrandColor';
import { FLineIcon } from '../FLineIcons';
import GoalCardV2 from './GoalCardV2';
import './GoalsWorkspaceV2.css';

function shuffle(list) {
    const arr = [...list];
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

function defaultTargetDate() {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    d.setHours(12, 0, 0, 0);
    return d;
}

function areaGoalCount(selectedGoals, areaIndex) {
    const idx = Number(areaIndex);
    // Count only the 3 goal slots for this F (avoids inflated totals from duplicates)
    return [0, 1, 2].filter((slot) => {
        const g = (selectedGoals || []).find(
            (x) =>
                Number(x.areaId) === idx &&
                Number(x.recomendationIndex) === slot
        );
        return (
            g &&
            String(g.text || '').trim() !== '' &&
            g.date != null &&
            g.date !== '' &&
            g.date !== 'null'
        );
    }).length;
}

function formatScore(f) {
    if (!f) return null;
    const n = Number(f.score);
    if (Number.isNaN(n)) return null;
    return n.toFixed(1);
}

/** Real F name when loaded; null avoids generic "Area" copy. */
function resolveAreaLabel(f) {
    const name = String(f?.name || '').trim();
    if (!name || name.toLowerCase() === 'area') return null;
    return name;
}

function resolveNextLabel(f) {
    const name = String(f?.name || '').trim();
    if (!name || name.toLowerCase() === 'next') return null;
    return name;
}

/**
 * Portal V2 — Step 2 goal workspace (classic RecomendationSection stays on /steps).
 */
export default function GoalsWorkspaceV2({
    AllState,
    saveRecomendedGoals,
    onSelectF,
    lastReachedIndex = 0,
}) {
    const currItem = AllState.Fs[AllState.CurrentF] || {};
    const accent = readableFColor(currItem.name, currItem.color || '#15c285');
    const nextItem = AllState.Fs[AllState.CurrentF + 1] || null;
    const areaLabel = resolveAreaLabel(currItem);
    const areaName = areaLabel || 'Area';
    const areaScore = formatScore(currItem);
    const navRef = useRef(null);

    const validCount = areaGoalCount(
        AllState.SelectedGoals,
        AllState.CurrentF
    );

    const [showError, setShowError] = useState(false);
    const [errorObj, setErrorObj] = useState({
        ModalErrorTitle: 'You are almost there',
        ModalErrorDesc: 'You need to add 3 goals to continue',
    });
    const [saving, setSaving] = useState(false);
    const [suggesting, setSuggesting] = useState(false);

    const ready = validCount === 3;
    const progress = Math.min(3, validCount) / 3;

    useEffect(() => {
        const rail = navRef.current;
        if (!rail) return;
        const active = rail.querySelector('.pv2-goals-ws__f.is-active');
        if (!active || typeof active.scrollIntoView !== 'function') return;
        active.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        });
    }, [AllState.CurrentF]);

    /** Fill empty goal slots with shuffled API recommendedGoals (do not overwrite). */
    function suggestGoals() {
        const pool = shuffle(
            (currItem.recommendedGoals || [])
                .map((g) => (g && g.text != null ? String(g.text).trim() : ''))
                .filter((t) => t.length > 0)
        );

        if (!pool.length) {
            setErrorObj({
                ModalErrorTitle: 'No recommendations yet',
                ModalErrorDesc:
                    'We could not load suggested goals for this area. Try again in a moment, or write your own.',
            });
            setShowError(true);
            return;
        }

        const areaId = AllState.CurrentF;
        const next = {
            ...AllState,
            SelectedGoals: [...AllState.SelectedGoals],
        };

        const used = new Set(
            next.SelectedGoals.filter(
                (g) =>
                    g.areaId === areaId && String(g.text || '').trim() !== ''
            ).map((g) => String(g.text).trim().toLowerCase())
        );

        const emptySlots = [0, 1, 2].filter((slot) => {
            const existing = next.SelectedGoals.find(
                (g) => g.areaId === areaId && g.recomendationIndex === slot
            );
            return !existing || String(existing.text || '').trim() === '';
        });

        if (!emptySlots.length) {
            setErrorObj({
                ModalErrorTitle: 'You are set',
                ModalErrorDesc:
                    'All 3 goal slots already have text. Edit them anytime, or clear a slot to suggest again.',
            });
            setShowError(true);
            return;
        }

        setSuggesting(true);
        const available = pool.filter(
            (t) => !used.has(t.toLowerCase())
        );
        const picks = (available.length ? available : pool).slice(
            0,
            emptySlots.length
        );

        emptySlots.forEach((slot, i) => {
            const text = picks[i % picks.length];
            const idx = next.SelectedGoals.findIndex(
                (g) => g.areaId === areaId && g.recomendationIndex === slot
            );
            if (idx >= 0) {
                next.SelectedGoals[idx] = {
                    ...next.SelectedGoals[idx],
                    text,
                    date:
                        next.SelectedGoals[idx].date != null
                            ? next.SelectedGoals[idx].date
                            : defaultTargetDate(),
                };
            } else {
                next.SelectedGoals.push({
                    recomendationIndex: slot,
                    text,
                    areaId,
                    date: defaultTargetDate(),
                    Id: next.SelectedGoals.length,
                });
            }
            used.add(text.toLowerCase());
        });

        AllState.changeData(next);
        window.setTimeout(() => setSuggesting(false), 280);
    }

    function nextF() {
        if (!ready) {
            setErrorObj({
                ModalErrorTitle: 'You are almost there',
                ModalErrorDesc:
                    'Add 3 goals with target dates to continue.',
            });
            setShowError(true);
            return;
        }

        const fromF = AllState.CurrentF;
        const next = { ...AllState };
        if (next.CurrentF === 6) {
            next.CurrentStep2 += 1;
        } else {
            next.CurrentF += 1;
            next.LastReachedF =
                next.CurrentF > next.LastReachedF
                    ? next.CurrentF
                    : next.LastReachedF;
        }
        // Switch F immediately; save previous area in background
        AllState.changeData(next);

        saveRecomendedGoals({ areaId: fromF, silent: true }).then(
            (ok) => {
                if (!ok) {
                    setErrorObj({
                        ModalErrorTitle: 'Couldn’t save goals',
                        ModalErrorDesc:
                            'Please check your connection and try again.',
                    });
                    setShowError(true);
                }
            }
        );
    }

    async function saveLater() {
        setSaving(true);
        try {
            await saveRecomendedGoals();
        } finally {
            setSaving(false);
        }
    }

    const ctaLabel =
        AllState.CurrentF >= 6
            ? 'Continue'
            : resolveNextLabel(nextItem)
              ? `Continue to ${resolveNextLabel(nextItem)}`
              : 'Continue';

    const titleText = areaLabel ? `Set 3 ${areaLabel} goals` : 'Set 3 goals';
    const leadText = areaLabel
        ? `Add 3 goals that will improve your ${areaLabel}.`
        : 'Add 3 goals for this area.';
    const goalPlaceholder = areaLabel
        ? `Write your ${areaLabel} goal`
        : 'Write your goal';

    const emptySlots = [0, 1, 2].filter((slot) => {
        const existing = AllState.SelectedGoals.find(
            (g) =>
                g.areaId === AllState.CurrentF &&
                g.recomendationIndex === slot
        );
        return !existing || String(existing.text || '').trim() === '';
    }).length;

    const handleSelectF = (index) => {
        if (typeof onSelectF !== 'function') return;
        if (index === AllState.CurrentF) return;
        if (index > lastReachedIndex) return;
        onSelectF(index);
    };

    return (
        <div
            className="pv2-goals-ws"
            style={{ ['--pv2-goals-accent']: accent }}
        >
            <nav
                className="pv2-goals-ws__fnav"
                aria-label="Life areas"
                ref={navRef}
            >
                {(AllState.Fs || []).map((f, i) => {
                    const fAccent = readableFColor(
                        f.name,
                        f.color || '#15c285'
                    );
                    const done = areaGoalCount(AllState.SelectedGoals, i) >= 3;
                    const locked = i > lastReachedIndex;
                    const active = i === AllState.CurrentF;
                    return (
                        <button
                            key={f.areaId || f.name || i}
                            type="button"
                            className={`pv2-goals-ws__f${
                                active ? ' is-active' : ''
                            }${done ? ' is-done' : ''}${
                                locked ? ' is-locked' : ''
                            }`}
                            style={{ ['--f-accent']: fAccent }}
                            disabled={locked}
                            aria-current={active ? 'true' : undefined}
                            aria-label={`${f.name || 'Area'}${
                                done ? ', goals complete' : ''
                            }`}
                            onClick={() => handleSelectF(i)}
                        >
                            <span
                                className="pv2-goals-ws__f-ico"
                                aria-hidden="true"
                            >
                                <FLineIcon name={f.name} index={i} />
                            </span>
                            <span className="pv2-goals-ws__f-name">
                                {f.name}
                            </span>
                            {done ? (
                                <span
                                    className="pv2-goals-ws__f-check"
                                    aria-hidden="true"
                                >
                                    ✓
                                </span>
                            ) : null}
                        </button>
                    );
                })}
            </nav>

            <div className="pv2-goals-ws__panel">
            <div
                className="pv2-goals-ws__score"
                aria-label={`${areaName} score and goal progress`}
            >
                <div className="pv2-goals-ws__score-main">
                    <span className="pv2-goals-ws__score-name">{areaName}</span>
                    {areaScore != null ? (
                        <strong className="pv2-goals-ws__score-val">
                            {areaScore}
                        </strong>
                    ) : null}
                </div>
                <div className="pv2-goals-ws__score-meta">
                    <span>
                        {ready
                            ? '3 of 3 goals added'
                            : `${validCount} of 3 goals added`}
                    </span>
                    <div
                        className="pv2-goals-ws__score-bar"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={3}
                        aria-valuenow={validCount}
                    >
                        <span style={{ width: `${progress * 100}%` }} />
                    </div>
                </div>
            </div>

            <header className="pv2-goals-ws__head">
                <div className="pv2-goals-ws__titles">
                    <p className="pv2-goals-ws__eyebrow">Step 2 · Goals</p>
                    <h2 className="pv2-goals-ws__title">{titleText}</h2>
                    <p className="pv2-goals-ws__lead">{leadText}</p>
                    <p className="pv2-goals-ws__lead pv2-goals-ws__lead--mobile">
                        {leadText}
                    </p>
                    <p className="pv2-goals-ws__emotion">
                        Make them specific, meaningful, and realistic.
                    </p>
                </div>

                <div
                    className={`pv2-goals-ws__progress${
                        ready ? ' is-ready' : ''
                    }`}
                    aria-label={`Goals completed ${validCount} of 3`}
                >
                    <span className="pv2-goals-ws__progress-kicker">
                        Added
                    </span>
                    <strong>
                        {validCount}
                        <em> / 3</em>
                    </strong>
                    <div
                        className="pv2-goals-ws__bar"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={3}
                        aria-valuenow={validCount}
                    >
                        <span style={{ width: `${progress * 100}%` }} />
                    </div>
                    <span className="pv2-goals-ws__progress-note">
                        {ready
                            ? 'Your 3 goals are ready.'
                            : leadText}
                    </span>
                </div>
            </header>

            <button
                type="button"
                className={`pv2-goals-ws__inspire${
                    suggesting ? ' is-busy' : ''
                }`}
                onClick={suggestGoals}
            >
                <span className="pv2-goals-ws__inspire-ico" aria-hidden="true">
                    ✦
                </span>
                <span className="pv2-goals-ws__inspire-full">
                    {suggesting
                        ? 'Suggesting…'
                        : emptySlots === 3
                          ? areaLabel
                              ? `Suggest ${areaLabel} goals`
                              : 'Suggest goals'
                          : emptySlots > 0
                            ? `Fill ${emptySlots} empty goal${
                                  emptySlots === 1 ? '' : 's'
                              }`
                            : areaLabel
                              ? `Suggest ${areaLabel} goals`
                              : 'Suggest goals'}
                </span>
                <span className="pv2-goals-ws__inspire-short">
                    {suggesting ? 'Suggesting…' : 'Suggest goals for me'}
                </span>
            </button>

            <div className="pv2-goals-ws__cards">
                {[0, 1, 2].map((i) => (
                    <GoalCardV2
                        key={`${AllState.CurrentF}-${i}`}
                        GoalIndex={i}
                        AllState={AllState}
                        areaName={areaLabel || ''}
                        accent={accent}
                        placeholder={goalPlaceholder}
                    />
                ))}
            </div>

            <div className="pv2-goals-ws__actions">
                <button
                    type="button"
                    className="pv2-goals-ws__save"
                    onClick={saveLater}
                >
                    {saving ? 'Saving…' : 'Save and continue later'}
                </button>
                <div className="pv2-goals-ws__cta-wrap">
                    <button
                        type="button"
                        className={`pv2-goals-ws__cta${
                            ready ? ' is-ready' : ' is-disabled'
                        }`}
                        onClick={nextF}
                        aria-disabled={!ready}
                    >
                        {ready
                            ? ctaLabel
                            : 'Add all 3 goals to continue'}
                    </button>
                </div>
            </div>
            </div>

            <ModalErrorV2
                show={showError}
                setShowError={setShowError}
                ModalErrorTitle={errorObj.ModalErrorTitle}
                ModalErrorDesc={errorObj.ModalErrorDesc}
            />
        </div>
    );
}
